import {
    combineEpics,
    createEpicMiddleware,
    StateObservable,
} from "redux-observable"
import { Observable } from "rxjs"
import { Action } from "redux"
import { GlobalState } from "./store"

import addinsEpics from "./components/addins/epics"
import authEpics from "./components/auth/epics"
import bookmarksEpics from "./components/bookmarks/epics"
import cardViewEpics from "./components/cardview/epics"
import conceptsPageEpics from "./components/conceptspage/epics"
import contextualHelpEpics from "./components/contextualhelp/epics"
import contractsEpics from "./components/contracts/epics"
import deploymentEpics from "./components/deployment/epics"
import indicatorsEpics from "./components/indicators/epics"
import inlineAttachmentEpics from "./components/inlineattachments/epics"
import navigationEpics from "./components/applicationheader/components/navigation/epics"
import notificationRootEpics from "./components/notifications/epics"
import resourceFooterRootEpics from "./components/resourcefooter/epics"
import resourceHeaderRootEpics from "./components/resourceheader/epics"
import searchRootEpics from "./components/search/epics"
import shortcutsEpics from "./components/shortcuts/epics"
import smartLocationEpics from "./components/applicationheader/components/smartlocation/epics"
import tutorialEpics from "./components/tutorial/epics"
import userMenuEpics from "./components/applicationheader/components/usermenu/epics"
import welcomePageEpics from "./components/welcomepage/epics"
import workspaceCoverPageEpics from "./components/workspacecoverpage/epics"
import workspaceListPageEpics from "./components/workspacelistpage/epics"

import {
    attachmentsAPI,
    authAPI,
    bookmarkAPI,
    conceptsAPI,
    contractsAPI,
    datalistAPI,
    deploymentAPI,
    gdlAPI,
    gridAPI,
    indicatorAPI,
    ipeAPI,
    fieldsAPI,
    itemboxAPI,
    relativeSiblingsAPI,
    searchAPI,
    shortcutAPI,
    tutorialAPI,
    userMenuAPI,
    navigationAPI,
    workspacesAPI,
} from "./apis"
import { ITenForceWindow } from "./global"

interface AddinsDependencies {
    deploymentAPI: typeof deploymentAPI
}

interface AuthDependencies {
    authAPI: typeof authAPI
    windowLocation: Location
}

interface BookmarksDependencies {
    bookmarkAPI: typeof bookmarkAPI
}

interface CardViewDependencies {
    attachmentsAPI: typeof attachmentsAPI
    ipeAPI: typeof ipeAPI
    itemboxAPI: typeof itemboxAPI
    windowLocation: Location
}

interface ConceptsPageDependencies {
    conceptsAPI: typeof conceptsAPI
    filteringDelay: number
    gdlAPI: typeof gdlAPI
}

interface ContractsDependencies {
    contractsAPI: typeof contractsAPI
}

interface DeploymentDependencies {
    deploymentAPI: typeof deploymentAPI
}

interface IndicatorsDependencies {
    indicatorAPI: typeof indicatorAPI
    loadingDelay: number
}

interface InlineAttachmentDependencies {
    attachmentsAPI: typeof attachmentsAPI
    ipeAPI: typeof ipeAPI
}

interface NavigationDependencies {
    datalistAPI: typeof datalistAPI
    navigationAPI: typeof navigationAPI
    filteringDelay: number
    limit: number
}

interface NotificationsDependencies {
    flashingDelay: number
}

interface SmartLocationDependencies {
    relativeSiblingsAPI: typeof relativeSiblingsAPI
    filteringDelay: number
}

interface ResourceFooterDependencies {
    ipeAPI: typeof ipeAPI
    fieldsAPI: typeof fieldsAPI
}

interface ResourceHeaderDependencies {
    datalistAPI: typeof datalistAPI
    filteringDelay: number
    ipeAPI: typeof ipeAPI
}

interface SearchDependencies {
    searchAPI: typeof searchAPI
    suggestionsDelay: number
    // eslint-disable-next-line @typescript-eslint/ban-types
    windowOpen: Function
}

interface ShortcutsDependencies {
    shortcutAPI: typeof shortcutAPI
}

interface TutorialDependencies {
    tutorialAPI: typeof tutorialAPI
}

interface UserMenuDependencies {
    userMenuAPI: typeof userMenuAPI
}

interface WelcomePageDependencies {
    navigationAPI: typeof navigationAPI
    gridAPI: typeof gridAPI
}

interface WorkspaceCoverPageDependencies {
    bookmarkAPI: typeof bookmarkAPI
    gridAPI: typeof gridAPI
    navigationAPI: typeof navigationAPI
}

interface WorkspaceListPageDependencies {
    filteringDelay: number
    workspacesAPI: typeof workspacesAPI
}

export interface GlobalDependencies {
    addins: AddinsDependencies
    auth: AuthDependencies
    bookmarks: BookmarksDependencies
    cardView: CardViewDependencies
    conceptsPage: ConceptsPageDependencies
    contracts: ContractsDependencies
    deployment: DeploymentDependencies
    indicators: IndicatorsDependencies
    inlineAttachment: InlineAttachmentDependencies
    navigation: NavigationDependencies
    notifications: NotificationsDependencies
    smartLocation: SmartLocationDependencies
    resourceFooter: ResourceFooterDependencies
    resourceHeader: ResourceHeaderDependencies
    search: SearchDependencies
    shortcuts: ShortcutsDependencies
    tutorial: TutorialDependencies
    userMenu: UserMenuDependencies
    welcomePage: WelcomePageDependencies
    workspaceCoverPage: WorkspaceCoverPageDependencies
    workspaceListPage: WorkspaceListPageDependencies
}

declare let window: ITenForceWindow

export const dependencies: GlobalDependencies = {
    addins: {
        deploymentAPI,
    },
    auth: {
        authAPI,
        windowLocation: window.location,
    },
    bookmarks: {
        bookmarkAPI,
    },
    cardView: {
        attachmentsAPI,
        ipeAPI,
        itemboxAPI,
        windowLocation: window.location,
    },
    conceptsPage: {
        conceptsAPI,
        filteringDelay: 1000,
        gdlAPI,
    },
    contracts: {
        contractsAPI,
    },
    deployment: {
        deploymentAPI,
    },
    indicators: {
        indicatorAPI,
        loadingDelay: 500,
    },
    inlineAttachment: {
        attachmentsAPI,
        ipeAPI,
    },
    navigation: {
        datalistAPI,
        navigationAPI,
        limit: 25,
        filteringDelay: 1000,
    },
    notifications: {
        flashingDelay: 1000,
    },
    smartLocation: {
        relativeSiblingsAPI,
        filteringDelay: 1000,
    },
    resourceFooter: {
        ipeAPI,
        fieldsAPI,
    },
    resourceHeader: {
        datalistAPI,
        filteringDelay: 1000,
        ipeAPI,
    },
    search: {
        searchAPI,
        suggestionsDelay: 100,
        windowOpen: window.open,
    },
    shortcuts: {
        shortcutAPI,
    },
    tutorial: {
        tutorialAPI,
    },
    userMenu: {
        userMenuAPI,
    },
    welcomePage: {
        navigationAPI,
        gridAPI,
    },
    workspaceCoverPage: {
        bookmarkAPI,
        gridAPI,
        navigationAPI,
    },
    workspaceListPage: {
        filteringDelay: 1000,
        workspacesAPI,
    },
}

export declare interface Epic<
    Input extends Action = Action,
    Output extends Input = Input,
    State = GlobalState,
    Dependencies = GlobalDependencies,
> {
    (
        action$: Observable<Input | number>,
        state$: StateObservable<State>,
        dependencies: Dependencies,
    ): Observable<Output>
}

export const rootEpic = combineEpics(
    addinsEpics,
    authEpics,
    bookmarksEpics,
    cardViewEpics,
    conceptsPageEpics,
    contextualHelpEpics,
    contractsEpics,
    deploymentEpics,
    indicatorsEpics,
    inlineAttachmentEpics,
    navigationEpics,
    notificationRootEpics,
    smartLocationEpics,
    resourceFooterRootEpics,
    resourceHeaderRootEpics,
    searchRootEpics,
    shortcutsEpics,
    tutorialEpics,
    userMenuEpics,
    welcomePageEpics,
    workspaceCoverPageEpics,
    workspaceListPageEpics,
)

export const epicMiddleware = createEpicMiddleware<
    Action,
    Action,
    GlobalState,
    GlobalDependencies
>({ dependencies })

export default epicMiddleware
